<template>
  <div class="car-content car-main-tab-content" data-js="service-selection">
    <back-btn />

    <div class="car-selection-overview car-reduced-width" v-if="!loadingServices && !maintenance.show">
      <selected-vehicle-tile />
      <selected-region-tile />
    </div>

    <div class="car-form-container car-reduced-width">

      <spinner v-if="loadingServices" class="car-spinner car-main-content-spinner" />

      <div v-else-if="maintenance.show" class="car-service-selection">
        <div class="car-content-introduction">
          <span class="car-headline">
            {{$t('maintenanceSelection.title')}}
          </span>
          <p>
            {{$t('maintenanceSelection.description')}}
          </p>
        </div>
        <maintenance-selection v-if="maintenance.show" :maintenance="maintenance" @close="closeMaintenanceSelection()"/>
      </div>
      <div v-else class="car-service-selection">
        <div class="car-content-introduction">
          <span class="car-headline">
            {{$t('serviceSelection.title')}}
          </span>
          <p>
            {{$t('serviceSelection.description')}}
          </p>
        </div>
        <div class="car-service-selection-container">
          <selected-services :loading="loading"
                             :empty-offers-error="emptyOffersError"
                             :request-error="requestError"
                             @goNext="goNext"/>
          <div class="car-service-search-form">
            <label data-js="services__title">
              {{ $t('serviceSearch.title') }}
            </label>
            <search-bar
              :isServiceSearch="true"
              @search="searchService"
              :placeholderValue="$t('serviceSelection.search')"/>
          </div>

          <div class="car-error-message" v-if="displayServiceSearchResult && filteredServiceGroups.length === 0">{{
            $t('serviceSelection.noServiceFound') }}
          </div>
        </div>
      </div>
    </div>
    <div class="car-service-group-container" v-if="!loadingServices && !maintenance.show">
      <drop-down :options="serviceCategories"
                 :selected-option="selectedCategoryObj"
                 class="car-service-category car-reduced-width"
                 v-if="!displayServiceSearchResult"
                 @change="selectCategory"/>
      <div v-if="displayServiceSearchResult" class="car-service-groups">
        <service-groups :service-groups="filteredServiceGroups" :maintenance="maintenance"/>
      </div>
      <service-groups v-else :service-groups="serviceGroups" :maintenance="maintenance"/>
    </div>
    <div class="car-form-container car-reduced-width" v-if="!loadingServices && !maintenance.show">
      <div class="car-form-error-message" v-if="$v.$error">{{ $t('serviceSelection.title') }}</div>
      <div class="car-form-buttons car-buttons">
        <primary-btn
          @click="goNext"
          :loading="loading"
          class="car-btn-primary"
          data-js="search_service--button">
          {{$t('serviceSelection.button')}}
        </primary-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { uniqBy, map } from 'lodash';
import { mapGetters } from 'vuex';
import SearchBar from '@/components/SearchBar';
import Spinner from 'vue-simple-spinner';
import DropDown from '@/components/DropDown';
import MaintenanceSelection from '@/components/serviceSelection/MaintenanceSelection';
import SelectedServices from '@/components/serviceSelection/SelectedServices';
import ServiceGroups from '@/components/serviceSelection/ServiceGroups';
import BackBtn from '@/components/BackBtn';
import SelectedVehicleTile from '@/components/vehicleIdentification/SelectedVehicleTile';
import SelectedRegionTile from '@/components/serviceSelection/SelectedRegionTile';
import PrimaryBtn from '@/components/PrimaryBtn';
const { required } = require('vuelidate/lib/validators');

export default {
  name: 'ServiceSelection',
  data: function () {
    return {
      selectedCategoryObj: {
        name: ''
      },
      loadingServices: false,
      loading: false,
      requestError: false,
      emptyOffersError: false,
      selectedCategory: '',
      filteredServiceGroups: [],
      displayServiceSearchResult: false,
      maintenance: {
        show: false,
        loading: false,
        requestError: false,
        service: null
      },
      lastService: null
    }
  },
  validations: {
    selectedServices: { required }
  },
  components: {
    PrimaryBtn,
    BackBtn,
    SearchBar,
    Spinner,
    DropDown,
    SelectedServices,
    ServiceGroups,
    MaintenanceSelection,
    SelectedVehicleTile,
    SelectedRegionTile
  },
  methods: {
    getOfferList: function() {
      return this.$store.dispatch('offer/getOfferList')
        .then((res) => {
          if (res.data.length === 0) {
            this.emptyOffersError = true;
            return res;
          }
          this.$store.dispatch('vehicleEquipments/skipEquipmentSelection', true);

          this.$router.push({
            name: 'OfferSelection',
            params: { searchKey: res.data[0].offerSearchKey }
          });
        })
        .catch(() => { this.requestError = true })
        .finally(() => { this.loading = false });
    },
    goNext() {
      this.$v.$touch();
      if (this.$v.$invalid || this.loading) { return }
      if (this.selectedServices.length) {
        this.loading = true;
        if (this.$store.getters['vehicleIdentification/vehicleType'].vin) {
          // if selected vehicle has vin -> user doesn't have to select equipments -> skip to offer selection page
          return this.getOfferList();
        } else {
          return this.checkEquipmentSelectionStep();
        }
      }
    },
    checkEquipmentSelectionStep() {
      // checks if equipment selection steps need to be shown by getting all avs from selected equipments
      // if not then skip to offer selection page
      this.requestError = false;
      let defaultEquipments;
      return this.$store.dispatch('vehicleEquipments/getEngineAV')
        .then((res) => {
          defaultEquipments = [res];
          this.$store.dispatch('vehicleEquipments/setDefaultAvEquipments', defaultEquipments); // just save those equipment objects in the store
          let selectedAvIds = defaultEquipments.map(e => e.id);
          return this.$store.dispatch('vehicleEquipments/getAvsByService', selectedAvIds) // here check if the equipments have some options for user to select
        })
        .then((res) => {
          if (res.data.options && res.data.options.length !== 0) {
            // if user has to select more details about equipments to get offers, show equipment selection route
            this.$store.dispatch('vehicleEquipments/skipEquipmentSelection', false);
            this.loading = false;
            this.$router.push('equipment-selection');
          } else {
            // otherwise just get offers
            const equipmentList = map(defaultEquipments, (e) => {
              return {
                equipmentCode: e.av,
                selected: true
              };
            });
            this.$store.dispatch('vehicleEquipments/skipEquipmentSelection', true);
            this.$store.dispatch('vehicleEquipments/setVehicleEquipmentList', equipmentList);
            return this.getOfferList();
          }
        })
        .catch((err) => {
          this.requestError = true;
          return Promise.reject(new Error(err));
        })
        .finally(() => {
          this.loading = false;
        })
    },
    initLoad() {
      this.loadingServices = true;
      this.$store.dispatch('vehicleServices/configureTopServices')
        .then(() => {
          this.selectedCategoryObj = this.serviceCategories[0];
        })
        .catch((err) => {
          this.requestError = true;
          return Promise.reject(new Error(err));
        })
        .finally(() => {
          this.loadingServices = false;
        });
    },
    getServiceGroupsByService(serviceText) {
      const regex = new RegExp(serviceText, 'i');
      let filteredServiceGroups = [];
      this.serviceCategories.map(category => {
        category.serviceGroups.map(serviceGroup => {
          let services = serviceGroup.services.filter(service => {
            return regex.test(this.$options.filters['optionTranslate'](service));
          });
          if (services.length) {
            let group = {
              name: serviceGroup.name,
              translations: serviceGroup.translations,
              services: services
            };
            filteredServiceGroups.push(group);
          }
        })
      });
      return uniqBy(filteredServiceGroups, 'name');
    },
    removeService(service) {
      this.$store.dispatch('vehicleServices/removeService', service);
    },
    searchService(serviceText) {
      let trimmedServiceText = serviceText.trim();
      if (trimmedServiceText.length) {
        this.displayServiceSearchResult = true;
        this.selectedCategory = '';
        this.filteredServiceGroups = this.getServiceGroupsByService(trimmedServiceText);
      } else {
        this.displayServiceSearchResult = false;
      }
    },
    selectCategory(option) {
      this.selectedCategoryObj = option;
      this.displayServiceSearchResult = false;
      this.selectedCategory = option.name;
    },
    goToVehicleSelection() {
      this.$router.push({ name: 'CatalogIdentification' });
    },
    closeMaintenanceSelection() {
      this.maintenance.show = false;
    }
  },
  computed: {
    serviceGroups() {
      const serviceCategories = this.$store.getters['vehicleServices/topServiceCategoryGroup'].serviceCategories;
      if (serviceCategories && serviceCategories.length >= 1) {
        let selectedCategory = serviceCategories.find(category => category.name === this.selectedCategory);
        if (selectedCategory) {
          return selectedCategory.serviceGroups;
        } else {
          this.selectCategory(serviceCategories[0]);
          return serviceCategories[0].serviceGroups;
        }
      } else {
        return [];
      }
    },
    serviceCategories() {
      return this.$store.getters['vehicleServices/topServiceCategoryGroup'].serviceCategories;
    },
    selectedServices() {
      return this.$store.getters['vehicleServices/selectedServices'];
    },
    ...mapGetters('vehicleIdentification', {
      vehicleSelectedFlag: 'vehicleSelectedFlag',
      vehicleType: 'vehicleType'
    })
  },
  created() {
    this.initLoad();
  }
}
</script>
